import React, { useEffect, useState } from "react";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { FilterChips, tNegocio } from "./shared";
import { money } from "./helpers";
import { useStyles } from "./styles";

const t = (k) => tNegocio(`pages.growth.${k}`);

const KIND = {
  new: "🆕",
  drop: "🔥",
  stock: "📦",
  digest: "📣",
  spot: "🌟",
  message: "💬",
  campaign: "📢",
  cart: "🛒",
};

const pct = (a, b) => (b ? `${Math.round((a / b) * 1000) / 10}%` : "—");

function Stat({ label, value, sub }) {
  return (
    <div style={{ minWidth: 150, padding: "10px 14px", borderRadius: 10, background: "rgba(127,127,127,0.08)" }}>
      <div style={{ fontSize: 12, opacity: 0.75 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800 }}>{value}</div>
      {sub ? <div style={{ fontSize: 12, opacity: 0.7 }}>{sub}</div> : null}
    </div>
  );
}

export default function GrowthSection({ settings, setSettings, onSave }) {
  const classes = useStyles();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    api
      .get("/negocio/growth", { params: { days } })
      .then(({ data: d }) => alive && setData(d))
      .catch(toastError);
    return () => {
      alive = false;
    };
  }, [days]);

  const set = (patch) => setSettings((s) => ({ ...s, ...patch }));
  const promo = data?.promo?.totals || { sent: 0, clicks: 0, paid: 0, revenue: 0 };
  const ref = data?.referral?.totals || { invited: 0, converted: 0 };
  const isPercent = settings.referralRewardMode === "percent";
  const cur = String(settings.currencyLocal || "COP").toUpperCase();
  const fxCur = String(settings.currencyFx || "USD").toUpperCase();
  const rate = Number(settings.exchangeRate) > 0 ? Number(settings.exchangeRate) : 1;
  const exampleSale = 20000;
  const pctOfExample = (exampleSale * Number(settings.referralRewardAmount || 0)) / 100;

  return (
    <div>
      <FormControlLabel
        control={
          <Switch color="primary" checked={!!settings.referralEnabled} onChange={(e) => set({ referralEnabled: e.target.checked })} />
        }
        label={t("referralEnabled")}
      />
      <div className={classes.captionBlock} style={{ margin: "0 0 8px" }}>
        {t("referralHint")}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        <TextField
          select
          margin="dense"
          variant="outlined"
          label={t("rewardMode")}
          value={isPercent ? "percent" : "fixed"}
          onChange={(e) => set({ referralRewardMode: e.target.value })}
          disabled={!settings.referralEnabled}
          SelectProps={{ native: true }}
          style={{ minWidth: 220 }}
        >
          <option value="fixed">{t("rewardModeFixed").replace("{cur}", cur)}</option>
          <option value="percent">{t("rewardModePercent")}</option>
        </TextField>
        <TextField
          margin="dense"
          variant="outlined"
          type="number"
          label={`${t("rewardAmount")} (${isPercent ? "%" : cur})`}
          value={settings.referralRewardAmount ?? 0}
          onChange={(e) => set({ referralRewardAmount: e.target.value })}
          helperText={isPercent ? t("rewardPercentHint") : t("rewardAmountHintLocal").replace("{cur}", cur)}
          disabled={!settings.referralEnabled}
          inputProps={isPercent ? { min: 0, max: 100, step: "1" } : { min: 0, step: "500" }}
          style={{ minWidth: 220 }}
        />
        <TextField
          margin="dense"
          variant="outlined"
          type="number"
          label={`${t("friendReward")} (${isPercent ? "%" : cur})`}
          value={settings.referralFriendReward ?? 0}
          onChange={(e) => set({ referralFriendReward: e.target.value })}
          helperText={t("friendRewardHint")}
          disabled={!settings.referralEnabled}
          inputProps={isPercent ? { min: 0, max: 100, step: "1" } : { min: 0, step: "500" }}
          style={{ minWidth: 260 }}
        />
      </div>
      {settings.referralEnabled && !(Number(settings.referralRewardAmount) > 0) ? (
        <div style={{ color: "#d32f2f", fontSize: 13, marginTop: 4 }}>{t("rewardZeroWarning")}</div>
      ) : settings.referralEnabled ? (
        <div className={classes.captionBlock} style={{ marginTop: 4 }}>
          {isPercent
            ? t("rewardPercentExample")
                .replace("{pct}", Number(settings.referralRewardAmount))
                .replace("{sale}", `${money(exampleSale)} ${cur}`)
                .replace("{wallet}", `${money(pctOfExample)} ${cur}`)
                .replace("{fx}", `${(Math.round((pctOfExample / rate) * 100) / 100).toFixed(2)} ${fxCur}`)
            : t("rewardFixedExample")
                .replace("{amount}", `${money(Number(settings.referralRewardAmount))} ${cur}`)
                .replace("{fx}", `${(Math.round((Number(settings.referralRewardAmount) / rate) * 100) / 100).toFixed(2)} ${fxCur}`)}
        </div>
      ) : null}

      <FormControlLabel
        style={{ marginTop: 16 }}
        control={
          <Switch
            color="primary"
            checked={settings.cartReminderEnabled !== false && settings.cartReminderEnabled !== 0}
            onChange={(e) => set({ cartReminderEnabled: e.target.checked })}
          />
        }
        label={t("cartEnabled")}
      />
      <div className={classes.captionBlock} style={{ margin: "0 0 12px" }}>
        {t("cartHint")}
      </div>

      <FormControlLabel
        control={
          <Switch
            color="primary"
            checked={settings.autoRenewWalletEnabled !== false && settings.autoRenewWalletEnabled !== 0}
            onChange={(e) => set({ autoRenewWalletEnabled: e.target.checked })}
            disabled={settings.pointsSystemEnabled === false}
          />
        }
        label={t("autoRenewEnabled")}
      />
      <div className={classes.captionBlock} style={{ margin: "0 0 12px" }}>
        {settings.pointsSystemEnabled === false ? t("autoRenewNeedsWallet") : t("autoRenewHint")}
      </div>

      <FormControlLabel
        control={
          <Switch
            color="primary"
            checked={!!settings.reactivationEnabled}
            onChange={(e) => set({ reactivationEnabled: e.target.checked })}
          />
        }
        label={t("reactivationEnabled")}
      />
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
        <TextField
          margin="dense"
          variant="outlined"
          type="number"
          label={t("reactivationDays")}
          value={settings.reactivationDays ?? 15}
          onChange={(e) => set({ reactivationDays: e.target.value })}
          disabled={!settings.reactivationEnabled}
          inputProps={{ min: 7, max: 180, step: 1 }}
          style={{ width: 220 }}
        />
      </div>
      <div className={classes.captionBlock} style={{ margin: "0 0 12px" }}>
        {t("reactivationHint")}
      </div>

      <div className={classes.captionBlock} style={{ fontWeight: 700, margin: "16px 0 4px" }}>
        {t("supportTitle")}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        <TextField
          margin="dense"
          variant="outlined"
          type="number"
          label={t("guaranteeHours")}
          value={settings.guaranteeHours ?? 24}
          onChange={(e) => set({ guaranteeHours: e.target.value })}
          inputProps={{ min: 1, max: 720, step: 1 }}
          style={{ width: 220 }}
        />
        <TextField
          margin="dense"
          variant="outlined"
          label={t("supportResponseText")}
          placeholder={t("supportResponsePlaceholder")}
          value={settings.supportResponseText || ""}
          onChange={(e) => set({ supportResponseText: e.target.value })}
          inputProps={{ maxLength: 255 }}
          style={{ flex: "1 1 320px" }}
        />
        <TextField
          margin="dense"
          variant="outlined"
          type="number"
          label={t("stockAlertDays")}
          value={settings.stockAlertDays ?? 3}
          onChange={(e) => set({ stockAlertDays: e.target.value })}
          helperText={t("stockAlertHint")}
          inputProps={{ min: 0, max: 30, step: 1 }}
          style={{ width: 260 }}
        />
      </div>
      <div className={classes.captionBlock} style={{ margin: "4px 0 12px" }}>
        {t("supportHint")}
      </div>

      <Button variant="contained" color="primary" onClick={() => onSave()}>
        {t("save")}
      </Button>

      <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "28px 0 10px", flexWrap: "wrap" }}>
        <div className={classes.captionBlock} style={{ fontWeight: 800, margin: 0 }}>
          {t("statsTitle").replace("{days}", days)}
        </div>
        <FilterChips
          value={days}
          onChange={setDays}
          options={[
            { value: 7, label: "7d" },
            { value: 30, label: "30d" },
            { value: 90, label: "90d" },
          ]}
        />
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
        <Stat label={t("promoSent")} value={promo.sent} />
        <Stat label={t("promoClicks")} value={promo.clicks} sub={pct(promo.clicks, promo.sent)} />
        <Stat label={t("promoPaid")} value={promo.paid} sub={pct(promo.paid, promo.clicks)} />
        <Stat label={t("promoRevenue")} value={money(promo.revenue)} />
        <Stat label={t("invited")} value={ref.invited} />
        <Stat label={t("converted")} value={ref.converted} sub={pct(ref.converted, ref.invited)} />
      </div>

      <div className={classes.captionBlock} style={{ fontWeight: 700, margin: "12px 0 4px" }}>
        {t("promosTitle")}
      </div>
      {data?.promo?.rows?.length ? (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("colPromo")}</TableCell>
              <TableCell align="right">{t("colSent")}</TableCell>
              <TableCell align="right">{t("colClicks")}</TableCell>
              <TableCell align="right">{t("colPaid")}</TableCell>
              <TableCell align="right">{t("colRevenue")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.promo.rows.map((r) => (
              <TableRow key={r.id}>
                <TableCell>
                  {KIND[r.kind] || "•"} {r.title}
                  <div style={{ fontSize: 11, opacity: 0.6 }}>{r.createdAt} UTC</div>
                </TableCell>
                <TableCell align="right">
                  {r.sent}
                  {r.chats ? ` +${r.chats}📢` : ""}
                </TableCell>
                <TableCell align="right">
                  {r.clicks} <span style={{ opacity: 0.6 }}>({pct(r.clicks, r.sent)})</span>
                </TableCell>
                <TableCell align="right">{r.paid}</TableCell>
                <TableCell align="right">{r.paid ? money(r.revenue) : "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <div className={classes.captionBlock}>{t("empty")}</div>
      )}

      <div className={classes.captionBlock} style={{ fontWeight: 700, margin: "20px 0 4px" }}>
        {t("topReferrers")}
      </div>
      {data?.referral?.rows?.length ? (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("colReferrer")}</TableCell>
              <TableCell>{t("colCode")}</TableCell>
              <TableCell align="right">{t("invited")}</TableCell>
              <TableCell align="right">{t("converted")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.referral.rows.map((r) => (
              <TableRow key={r.contactId}>
                <TableCell>{r.name || r.number}</TableCell>
                <TableCell>{r.referralCode || "—"}</TableCell>
                <TableCell align="right">{r.invited}</TableCell>
                <TableCell align="right">{r.converted}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <div className={classes.captionBlock}>{t("empty")}</div>
      )}
    </div>
  );
}
